import { FunctionComponentElement, ReactNode } from 'react'

import useRightsForOrgData from './useRightsForOrgData'
import childrenWithDisabledProp from '../utils/childrenWithDisabledProp'
import extractRightsByPaths from '../utils/extractRightsByPaths'
import { RightsPathUnrestricted } from '../types'

/**
 *  Для оборачивания компонентов которым требуются права доступа.
 *  ПО УМОЛЧАНИЮ:
 *  Возвращает null если нет прав даже на чтение.
 *  Добавляются дополнительные props:
 *    + disabled - true, пока права не загружены или есть права только на чтение
 */

type Props = {
  children: FunctionComponentElement<{ disabled?: boolean }>
  pathTo: RightsPathUnrestricted
  othersPaths?: Array<RightsPathUnrestricted>
  combineWithOthersPaths?: 'all' | 'any'
  readAffect?: 'hide' | 'disable' | 'ignore'
  editAffect?: 'hide' | 'disable' | 'ignore'
  viewOnHide?: ReactNode
  disabled?: boolean
}

const WithRightsForOrg = ({
  children,
  pathTo,
  othersPaths = [],
  combineWithOthersPaths = 'all',
  readAffect = 'hide',
  editAffect = 'disable',
  viewOnHide = null,
  disabled,
}: Props) => {
  const [{ su, rights, ready }] = useRightsForOrgData()

  if (disabled) {
    return childrenWithDisabledProp(children, true)
  }

  // права не готовы - disabled
  if (!ready) {
    if (readAffect === 'hide' && editAffect === 'hide') {
      return null
    }
    return childrenWithDisabledProp(children, true)
  }

  // полный доступ
  if (su) {
    return childrenWithDisabledProp(children, false)
  }

  const { edit, read } = extractRightsByPaths({
    paths: [pathTo, ...othersPaths],
    rights,
    combineWithOthersPaths,
  })

  const hide = (readAffect === 'hide' && !read) || (editAffect === 'hide' && !edit)
  if (hide) return viewOnHide

  const disable = (readAffect === 'disable' && !read) || (editAffect === 'disable' && !edit)
  if (disable) return childrenWithDisabledProp(children, true)

  return children
}

export default WithRightsForOrg
